import React, { createContext, useContext, useEffect, useEffectEvent, useState } from "react";
import { Auth, onIdTokenChanged, User } from "firebase/auth";
import UserProfile from "./UserProfile";
import useUserProfile from "./useUserProfile";
import { auth } from "@/firebase";

type UserContextState = {
    auth: Auth;
    isAuthLoading: boolean;
    isUserProfileLoading: boolean;
    user: User | null; // The firebase auth user object
    uid?: string; // The cached UID of the assumed user. May not be accurate, do not use for sensitive operations.
    userProfile?: UserProfile | null; // The firestore userprofile document, if it exists.
    userNeedsToCompleteProfile: boolean;
    isAnonymous: boolean;
};

const FirebaseAuthContext = createContext<UserContextState | null>(null);

function FirebaseAuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<UserContextState["user"]>(auth.currentUser);
    const [isAuthLoading, setIsAuthLoading] = useState(true);

    const uid = user?.uid;
    const isAnonymous = user?.isAnonymous ?? true;

    const { userProfile, isUserProfileLoading } = useUserProfile(user, isAuthLoading);

    const userNeedsToCompleteProfile = Boolean(!userProfile && !isUserProfileLoading && user && !isAuthLoading);

    const createAuthChangeSub = useEffectEvent(() => {
        const unsubscribe = onIdTokenChanged(auth, async (currentUser) => {
            console.log("Auth state changed:", currentUser);
            if (currentUser) {
                setIsAuthLoading(true);
                currentUser.getIdToken().then(() => {
                    setUser(currentUser);
                    setIsAuthLoading(false);
                });
            } else {
                setUser(null);
                setIsAuthLoading(false);
            }
        });
        return unsubscribe;
    });

    useEffect(() => {
        return createAuthChangeSub();
    }, []);

    return (
        <FirebaseAuthContext.Provider
            value={{
                isAuthLoading,
                isUserProfileLoading,
                user,
                uid,
                isAnonymous,
                userProfile,
                userNeedsToCompleteProfile,
                auth,
            }}
        >
            {children}
        </FirebaseAuthContext.Provider>
    );
}

function useFirebaseAuth(): UserContextState {
    const context = useContext(FirebaseAuthContext);
    if (context === null) {
        throw new Error("useFirebaseAuth must be used within a FirebaseAuthProvider");
    }
    return context;
}

export { FirebaseAuthProvider, useFirebaseAuth };
